import { faTrash } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button, CardV2 as Card, Input, Label } from '@feathr/components';
import { objectId } from '@feathr/hooks';

import { AD_TEXT_LIMITS } from '../../GoogleAdsCampaignEdit.utils';
import InputList from '../../InputList';
import AdPreview from '../AdPreview';
import type { IAd, IAdRowErrors, ISitelink } from '../AdStep.types';

interface IAdFieldsProps {
  ad: IAd;
  /** Messages for this ad's rows, keyed by row key. Absent while it is still loading. */
  adErrors?: IAdRowErrors;
  adNumber: number;
  /** The ad group's sitelinks, shown in the preview since every ad inherits them. */
  sitelinks: ISitelink[];
  /** The advertiser name Google shows above the display URL. */
  businessName?: string;
  onChange: (patch: Partial<Omit<IAd, 'key'>>) => void;
  onRemove: () => void;
  disabled?: boolean;
}

type TAdTextField = 'headlines' | 'descriptions';

/**
 * One responsive search ad: its headlines, descriptions, final URL, and a live preview
 * of how it will appear on a results page.
 *
 * @param props - {@link IAdFieldsProps}.
 */
function AdFields({
  ad,
  adErrors,
  adNumber,
  sitelinks,
  businessName,
  onChange,
  onRemove,
  disabled = false,
}: Readonly<IAdFieldsProps>): JSX.Element {
  const { t } = useTranslation();

  function handleTextChange(field: TAdTextField): (index: number, value: string) => void {
    return function (index: number, value: string): void {
      onChange({
        [field]: ad[field].map((item, i) => (i === index ? { ...item, text: value } : item)),
      });
    };
  }

  function handleTextRemove(field: TAdTextField): (index: number) => void {
    return function (index: number): void {
      onChange({ [field]: ad[field].filter((_, i) => i !== index) });
    };
  }

  function handleTextAdd(field: TAdTextField): () => void {
    return function (): void {
      onChange({ [field]: [...ad[field], { key: objectId(), text: '' }] });
    };
  }

  function handleFinalUrlChange(value?: string): void {
    onChange({ finalUrl: value ?? '' });
  }

  const isHeadlinesFull = ad.headlines.length >= AD_TEXT_LIMITS.headline.maxItems;
  const isDescriptionsFull = ad.descriptions.length >= AD_TEXT_LIMITS.description.maxItems;

  return (
    <Card name={`ad-${adNumber}`} width={'full'}>
      <Card.Header title={t('Ad {{adNumber}}', { adNumber })}>
        <Button
          aria-label={t('Remove ad {{adNumber}}', { adNumber })}
          disabled={disabled}
          name={`remove-ad-${adNumber}`}
          onClick={onRemove}
          tooltip={t('Remove ad')}
          type={'icon'}
        >
          <FontAwesomeIcon icon={faTrash} />
        </Button>
      </Card.Header>
      <Card.Content addVerticalGap={true}>
        {/* Wrapper keeps the section's 16px flex gap off InputListItem's own row margin. */}
        <div>
          <InputList
            errors={adErrors?.headlines}
            items={ad.headlines}
            label={t('headline')}
            maxItems={AD_TEXT_LIMITS.headline.maxItems}
            maxLength={AD_TEXT_LIMITS.headline.maxLength}
            minItems={AD_TEXT_LIMITS.headline.minItems}
            onChange={handleTextChange('headlines')}
            onRemove={handleTextRemove('headlines')}
            removeTooltip={t('At least {{minimum}} headlines are required.', {
              minimum: AD_TEXT_LIMITS.headline.minItems,
            })}
          />
        </div>
        <Button
          disabled={disabled || isHeadlinesFull}
          name={`ad-${adNumber}-add-headline`}
          onClick={handleTextAdd('headlines')}
          tooltip={
            isHeadlinesFull &&
            t('A maximum of {{maximum}} headlines can be added.', {
              maximum: AD_TEXT_LIMITS.headline.maxItems,
            })
          }
        >
          {t('Add headline')}
        </Button>
      </Card.Content>
      <Card.Content addVerticalGap={true}>
        <div>
          <InputList
            errors={adErrors?.descriptions}
            items={ad.descriptions}
            label={t('description')}
            maxItems={AD_TEXT_LIMITS.description.maxItems}
            maxLength={AD_TEXT_LIMITS.description.maxLength}
            minItems={AD_TEXT_LIMITS.description.minItems}
            onChange={handleTextChange('descriptions')}
            onRemove={handleTextRemove('descriptions')}
            removeTooltip={t('At least {{minimum}} descriptions are required.', {
              minimum: AD_TEXT_LIMITS.description.minItems,
            })}
            type={'textarea'}
          />
        </div>
        <Button
          disabled={disabled || isDescriptionsFull}
          name={`ad-${adNumber}-add-description`}
          onClick={handleTextAdd('descriptions')}
          tooltip={
            isDescriptionsFull &&
            t('A maximum of {{maximum}} descriptions can be added.', {
              maximum: AD_TEXT_LIMITS.description.maxItems,
            })
          }
        >
          {t('Add description')}
        </Button>
      </Card.Content>
      <Card.Content>
        <Input
          disabled={disabled}
          helpText={t('Search campaigns set the landing page per ad, not per campaign.')}
          label={t('Final URL')}
          onChange={handleFinalUrlChange}
          placeholder={t('e.g. https://example.org/programs')}
          required={true}
          type={'text'}
          validationError={adErrors?.finalUrl.length ? adErrors.finalUrl : undefined}
          value={ad.finalUrl}
        />
      </Card.Content>
      <Card.Content>
        <Label weight={'medium'}>{t('Preview')}</Label>
        <AdPreview
          ad={ad}
          businessName={businessName}
          name={`ad-${adNumber}-preview`}
          sitelinks={sitelinks}
        />
      </Card.Content>
    </Card>
  );
}

export default AdFields;
